import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FiArrowRight, FiCheckCircle } from "react-icons/fi";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Loader from "../../components/Loader/Loader";
import { getConsultationServices } from "../../api/consultationApi";
import { Helmet } from "react-helmet-async";
import {
  CATEGORY_META,
  CATEGORY_ORDER,
  formatINR,
  resolveImage,
} from "./consultationUtils";
import "./Consultation.css";

const FUNNEL = [
  ["Choose a service", "Pick the consultation that matches your need."],
  ["Choose your consultant", "Compare profiles, experience and fees."],
  ["Select date & time", "Book any free slot that suits you."],
  ["Share your details", "Birth details and your main concern."],
  ["Pay securely", "UPI, cards and net banking via PayU."],
  ["Get confirmation", "Booking details sent right after payment."],
];

export default function ConsultationHome() {
  const { category } = useParams();
  const navigate = useNavigate();
  const active = CATEGORY_ORDER.includes(category) ? category : "astrology";

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const result = await getConsultationServices();
        if (cancelled) return;
        if (!result.ok || !result.success)
          throw new Error(result.message || "Unable to load services");
        setServices(result.data || []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Unable to load services");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = services.filter((s) => s.category === active);

  // const { category } = useParams();
  // const navigate = useNavigate();
  // const active = CATEGORY_ORDER.includes(category) ? category : "astrology";

  const seoTitle = "Vedic Astrology Consultation Online | Vaidik Vidhya";

  const seoDescription =
    "Explore online Vedic astrology, numerology and Vastu consultations with experienced consultants. Compare services, fees and book your session online.";

  const canonicalUrl = `https://astrologicalsolutions.in/consultation/${active}`;

  const consultationSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: seoTitle,
    description: seoDescription,
    url: canonicalUrl,
    isPartOf: {
      "@type": "WebSite",
      name: "Astro Logical Solutions",
      url: "https://astrologicalsolutions.in/consultation",
    },
    about: {
      "@type": "Thing",
      name: "Vedic Astrology, Numerology and Vastu Consultations",
    },
  };

  return (
    <>
      <Helmet>
        <title>Vaidik Vidhya | Learn Vedic Knowledge & Spiritual Wisdom</title>
        <meta
          name="description"
          content="Explore Vaidik Vidhya to learn Vedic knowledge, astrology, numerology, Vastu and spiritual wisdom through practical courses and guided learning.
"
        />
        <link
          rel="canonical"
          href="https://astrologicalsolutions.in/consultation"
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Vedic Astrology Consultation Online | Vaidik Vidhya"
        />

        <meta
          property="og:description"
          content="Book online consultations for Vedic Astrology, Numerology and Vastu with experienced consultants. Compare services, explore fees and schedule your session."
        />
        <meta
          property="og:url"
          content="https://astrologicalsolutions.in/consultation"
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Astro Logical Solutions" />
        <meta
          property="og:image"
          content="https://astrologicalsolutions.in/og/consultation.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta
          name="twitter:image"
          content="https://astrologicalsolutions.in/og/consultation.jpg"
        />
        <script type="application/ld+json">
          {JSON.stringify(consultationSchema)}
        </script>
      </Helmet>
      <Header />
      {/* <section className="cn-hero">
        <div className="cn-hero-inner">
          <span className="cn-eyebrow">Vaidik Vidhya™ Consultations</span>
          <h1>Book a one-to-one consultation</h1>
          <p>
            Astrology, Numerology and Vastu guidance from experienced
            consultants. Read about the service, choose your consultant and book
            a slot in minutes.
          </p>
        </div>
      </section> */}

      <section className="consultation-banner">
        <img
          src="https://res.cloudinary.com/dbb2hkjri/image/upload/v1793273193/file_000000006220820ca0783bc71b7eace0_yqhxsa.png"
          alt="Consultation Banner"
          className="consultation-banner-img"
        />
      </section>

      <section className="cn-section">
        <div className="cn-container">
          <div
            className="cn-tabs"
            role="tablist"
            aria-label="Consultation categories"
          >
            {CATEGORY_ORDER.map((key) => (
              <button
                key={key}
                role="tab"
                aria-selected={active === key}
                className={`cn-tab ${active === key ? "is-active" : ""}`}
                onClick={() => navigate(`/consultation/${key}`)}
              >
                <span className="cn-tab-symbol" aria-hidden="true">
                  {CATEGORY_META[key].symbol}
                </span>
                {CATEGORY_META[key].label}
              </button>
            ))}
          </div>

          <p className="cn-tab-blurb">{CATEGORY_META[active].blurb}</p>

          {loading ? (
            <Loader label="Loading services..." />
          ) : error ? (
            <p className="cn-state">{error}</p>
          ) : visible.length === 0 ? (
            <p className="cn-state">
              Services for this category are coming soon.
            </p>
          ) : (
            <div className="cn-grid">
              {visible.map((s) => (
                <article className="cn-card" key={s.id}>
                  {s.imageUrl && (
                    <img
                      className="cn-card-img"
                      src={resolveImage(s.imageUrl)}
                      alt=""
                      loading="lazy"
                    />
                  )}
                  <h3>{s.title}</h3>
                  <p>{s.shortDescription}</p>
                  <div className="cn-card-foot">
                    <span className="cn-price">
                      {s.startingFee != null ? (
                        <>
                          <small>Starting</small> {formatINR(s.startingFee)}
                        </>
                      ) : (
                        <small>Consultants coming soon</small>
                      )}
                    </span>
                    <Link
                      className="cn-link"
                      to={`/consultation/${s.category}/${s.slug}`}
                    >
                      View details <FiArrowRight />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="cn-section cn-section-alt">
        <div className="cn-container">
          <h2 className="cn-h2">How booking works</h2>
          <ol className="cn-funnel">
            {FUNNEL.map(([title, text], i) => (
              <li key={title}>
                <span className="cn-funnel-num">{i + 1}</span>
                <div>
                  <h4>{title}</h4>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="cn-assure">
            <FiCheckCircle /> Secure payment · Instant confirmation · Slots held
            for you while you pay
          </p>
        </div>
      </section>

      {/* 
      <section className="cn-promo" aria-label="Mobile Numerology">
        <div className="cn-container cn-promo-inner">
          <div>
            <span className="cn-eyebrow">New · Vaidik Vidhya™</span>
            <h2>Personalised Mobile Number Consultation</h2>
            <p>
              A mobile number shortlisted from your horoscope and numerology
              profile, with guided SIM delivery. You can also check any number
              for free.
            </p>
          </div>
          <div className="cn-promo-actions">
            <Link
              className="cn-link cn-promo-btn"
              to="/mobile-numerology#customize"
            >
              Start customization <FiArrowRight />
            </Link>
            <Link
              className="cn-link cn-promo-btn"
              to="/mobile-numerology#checker"
            >
              Check a number free
            </Link>
            <Link
              className="cn-link cn-promo-btn"
              to="https://astrologicalsolutions.in/rudraksha-calculator"
            >
              Rudraksha Calculator.{" "}
            </Link>
          </div>
                  <img src="https://i.pinimg.com/1200x/9f/3f/04/9f3f04c22808bc351e8995c8af61acf4.jpg" alt="" />

        </div>
      </section> */}

      <section className="cn-promo" aria-label="Mobile Numerology">
        <div className="cn-container cn-promo-inner">
          <div className="cn-promo-content">
            <span className="cn-eyebrow">New · Vaidik Vidhya™</span>

            <h2>Personalised Mobile Number Consultation</h2>

            <p>
              A mobile number shortlisted from your horoscope and numerology
              profile, with guided SIM delivery. You can also check any number
              for free.
            </p>

            <div className="cn-promo-actions">
              <Link
                className="cn-link cn-promo-btn"
                to="/mobile-numerology#customize"
              >
                Start customization <FiArrowRight />
              </Link>

              <Link
                className="cn-link cn-promo-btn"
                to="/mobile-numerology#checker"
              >
                Check a number free
              </Link>

              <Link className="cn-link cn-promo-btn" to="/rudraksha-calculator">
                Rudraksha Calculator <FiArrowRight />
              </Link>
            </div>
          </div>

          <div className="cn-promo-image">
            <img
              src="https://i.pinimg.com/736x/00/6a/8e/006a8e94ac7a7a3e1e213500617402f6.jpg"
              alt="Rudraksha for spiritual guidance"
              loading="lazy"
            />
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
